import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { FlowEventEmitter } from '../../editor-composition/useFlowEvents';
import type { NodeSpec } from './NodeSpec';

export const functionalOutputsUiNodeSpec: NodeSpec<NodeCfg<'functionalOutputs'>> = {
    outputs: () => ({}),
    nodeCategory: 'function',
    kindLabel: 'Functional Outputs',
    canCopy: false,
    canDelete: false,
    validator: node => {
        const names = node.controlValues.outputs.map(output => output.name);
        if (!node.controlValues.outputs.every(output => output.name)) {
            return { outputs: 'One or more output names are empty' };
        }
        if (new Set(names).size !== names.length) {
            return { outputs: 'One or more outputs share a name' };
        }
        return true;
    },
    requiredSockets: (node, availableInputSockets) => {
        const availableOptions = [ 'finish', ...node.controlValues.outputs.map((_, index) => `input${index}`) ] as ('finish' | `input${number}`)[];
        return { required: [], conditionallyRequired: [ { kind: 'or', availableOptions: availableOptions.filter(socket => availableInputSockets.includes(socket)) } ] };
    },
    suppressNode: true,
    cfg: () => ({
        id: getUID(),
        disabled: false,
        kind: 'functionalOutputs',
        controlValues: {
            outputs: [],
        },
    }),
    controls: nodeCfg => ({
        outputs: defineControlOpts({
            cfg: {
                kind: 'button',
                useDialog: null,
                label: 'Add Output',
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'click': (_, { flowEventEmitter }) => flowEventEmitter.emit('updateNodeCfg', {
                    newNodeCfg: {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            outputs: [
                                ...nodeCfg.controlValues.outputs,
                                {
                                    name: '',
                                    dataType: 'string',
                                },
                            ],
                        },
                    },
                }),
            },
            index: 1,
            value: nodeCfg.controlValues.outputs,
        }),
    }),
    inputs: nodeCfg => {
        const updateOutputAt = (index: number, name: string, dataType: NodeCfg<'functionalOutputs'>['controlValues']['outputs'][number]['dataType'], flowEventEmitter: FlowEventEmitter) => {
            flowEventEmitter.emit('updateNodeCfg', {
                newNodeCfg: {
                    ...nodeCfg,
                    controlValues: {
                        ...nodeCfg.controlValues,
                        outputs: nodeCfg.controlValues.outputs.map((item, itemIndex) => itemIndex === index ? { name, dataType } : item),
                    },
                },
            });
        };

        const inputs: ReturnType<NodeSpec<NodeCfg<'functionalOutputs'>>['inputs']> = {
            finish: { socket: 'event', label: 'Finish' },
        };

        nodeCfg.controlValues.outputs.forEach((output, index) => {
            inputs[`input${index}`] = {
                socket: output.dataType,
                controlOpts: defineControlOpts({
                    cfg: {
                        valuePlaceholder: 'Output Name',
                        selectionPlaceholder: 'Output Type',
                        kind: 'deletableTextControlWithTypeSelector',
                        allowedDataTypes: [ 'string', 'number', 'boolean' ],
                    },
                    enableWithConnectedSocket: true,
                    value: { newValue: output.name, dataType: output.dataType },
                    on: {
                        'update:value': (change, { flowEventEmitter }) => updateOutputAt(index, change.newValue, change.dataType, flowEventEmitter),
                        'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                            direction: 'input',
                            removalMethod: {
                                kind: 'indexedIdentifier',
                                prefix: 'input',
                                index,
                            },
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    outputs: nodeCfg.controlValues.outputs.filter((_, itemIndex) => itemIndex !== index),
                                },
                            },
                        }),
                    },
                }),
            };
        });

        return inputs;
    },
};
